import { Box } from '@mantine/core'
import {
  type PropsWithChildren,
  type ReactNode,
  createContext,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react'

import classes from './styles.module.css'
import { type OpenModalOptions } from './types/OpenModalOptions'

type ModalsState = {
  openModal: (modal: ReactNode, options?: OpenModalOptions) => void
  closeModal: () => void
}

export const ModalsContext = createContext<ModalsState>({
  openModal: () => null,
  closeModal: () => null,
})

export function ModalsProvider({ children }: PropsWithChildren) {
  const portalRef = useRef<HTMLDivElement>(null)
  const [modal, setModal] = useState<ReactNode>(null)

  const checkBackdropAndClose = useCallback((e: Event) => {
    if (e.target === portalRef.current) {
      closeModal()
    }
  }, [])

  const checkEscAndClose = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal()
    }
  }, [])

  const closeModal = useCallback(() => {
    if (portalRef.current) {
      setModal(null)
      portalRef.current.classList.remove(classes.modalsPortalVisible)
      portalRef.current.removeEventListener('click', checkBackdropAndClose)
      window.removeEventListener('keyup', checkEscAndClose)
    }
  }, [])

  const openModal = useCallback(
    (modal: ReactNode, paramOptions?: OpenModalOptions) => {
      const options: OpenModalOptions = {
        closeOnBackdropClick: true,
        closeOnEscKey: true,
        ...paramOptions,
      }

      if (portalRef.current) {
        setModal(modal)
        portalRef.current.classList.add(classes.modalsPortalVisible)

        if (options.closeOnBackdropClick) {
          portalRef.current.addEventListener('click', checkBackdropAndClose)
        }

        if (options.closeOnEscKey) {
          window.addEventListener('keyup', checkEscAndClose)
        }
      }
    },
    []
  )

  const actions = useMemo(() => ({ closeModal, openModal }), [])

  return (
    <ModalsContext.Provider value={actions}>
      <Box
        ref={portalRef}
        className={classes.modalsPortal}
        data-testid='modals-portal'
      >
        {modal}
      </Box>
      {children}
    </ModalsContext.Provider>
  )
}
